import { useCallback, useEffect, createContext, useContext } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { LoadingSpinner } from '../../components';
import { useExamSession } from '../exam-session';
import { type ExamFormInput } from '../new-exam';
import { mapExamRoomPaperItemsToFormInput } from '../new-exam/utils';

interface ExamPreviewFormProviderProps {
  children: React.ReactNode;
}

export interface ExamPreviewFormContext {
  submitExam: () => Promise<void>;
}

const ExamPreviewFormSubmitContext = createContext<ExamPreviewFormContext | null>(null);

export const useExamPreviewFormOptional = () => {
  const submitContext = useContext(ExamPreviewFormSubmitContext);
  return submitContext;
};

export const useExamPreviewForm = () => {
  const formContext = useFormContext<ExamFormInput>();
  const submitContext = useContext(ExamPreviewFormSubmitContext);

  if (!submitContext) {
    throw new Error('useExamPreviewForm must be used within ExamPreviewFormProvider');
  }

  return { ...formContext, ...submitContext };
};

function ExamPreviewFormProvider({ children }: Readonly<ExamPreviewFormProviderProps>) {
  const formContext = useForm<ExamFormInput>({
    mode: 'onTouched',
    defaultValues: {},
  });
  const { examSession, loading: isExamSessionLoading } = useExamSession();

  useEffect(() => {
    if (!examSession) return;
    // TODO: Map generated Exam Room Paper Items to Form Input
    const formInput = mapExamRoomPaperItemsToFormInput(
      examSession?.examRoomAnswer?.examRoomExamPaperItems ?? null,
    );
    formContext.reset(formInput);
  }, [examSession, isExamSessionLoading, formContext]);

  const handleSubmitExam = useCallback(async () => undefined, [examSession]);

  const submitExam = useCallback(async () => {
    await formContext.handleSubmit(handleSubmitExam)();
  }, [formContext.handleSubmit, handleSubmitExam]);

  const contextValue: ExamPreviewFormContext = {
    submitExam,
  };

  if (isExamSessionLoading) {
    return (
      <div className='grid h-dvh w-screen place-items-center'>
        <LoadingSpinner />
      </div>
    );
  }
  return (
    <FormProvider {...formContext}>
      <ExamPreviewFormSubmitContext.Provider value={contextValue}>
        <form
          noValidate
          onSubmit={async (e: React.FormEvent<HTMLFormElement>) => {
            e.stopPropagation();
            await formContext.handleSubmit(handleSubmitExam)(e);
          }}
          className='flex h-full flex-col'
        >
          {children}
        </form>
      </ExamPreviewFormSubmitContext.Provider>
    </FormProvider>
  );
}

export { ExamPreviewFormProvider };
